import React, { useMemo, useState } from "react"
import { Button, ConfigProvider, Input, Modal, Segmented, Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { QC_BUILDER_DRAG_TYPE } from "stores/queryBuilderStore"
import {
  flattenCodebookForSelect,
  getBoolChildLabels,
} from "utils/codebookUtils"
import { FilterDef, newId } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  useDashboardStoreApi,
} from "features/Dashboard/runtime/dashboardStore"
import { WORKSPACE_THEME } from "features/Dashboard/components/workspaceTheme"

const CONTROL_OPTIONS: { value: FilterDef["control"]; label: string }[] = [
  { value: "multi", label: "Multi" },
  { value: "single", label: "Single" },
]

const lastSegment = (p: string): string => p.split("//").pop() ?? p

const toValues = (v: string | string[] | undefined | null): string[] =>
  v === undefined || v === null ? [] : Array.isArray(v) ? v : [v]

/**
 * Where the author defines the filters viewers get: label, one or several
 * categories, the default choice and the order. A variable is added from the
 * list or dropped in from the variables tree; the dialog has no mask, so the
 * tree stays usable while it is open. Every edit writes the whole list with
 * `setFilters`.
 */
const FilterDefinitionPopover: React.FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const store = useDashboardStoreApi()
  const filters = useDashboardStore((s) => s.document.filters)
  const codebook = useDashboardStore((s) => s.codebook)
  const setFilters = useDashboardStore((s) => s.setFilters)
  const setFilterSelection = useDashboardStore((s) => s.setFilterSelection)
  const [newVariable, setNewVariable] = useState<string | undefined>()
  const [note, setNote] = useState<string | null>(null)
  const [dropping, setDropping] = useState(false)

  // Only a variable with categories can filter; one already in use stays
  // listed but cannot be picked again.
  const candidates = useMemo(
    () =>
      flattenCodebookForSelect(codebook ?? []).filter(
        (o) => getBoolChildLabels(codebook, o.value).length > 0
      ),
    [codebook]
  )
  const variables = useMemo(() => {
    const used = new Set(filters.map((f) => f.variable))
    return candidates.map((o) => ({ ...o, disabled: used.has(o.value) }))
  }, [candidates, filters])
  const categoriesById = useMemo(
    () =>
      Object.fromEntries(
        filters.map((f) => [
          f.id,
          getBoolChildLabels(codebook, f.variable).map((l) => ({
            value: l,
            label: l,
          })),
        ])
      ),
    [filters, codebook]
  )

  const update = (i: number, patch: Partial<FilterDef>): void =>
    setFilters(filters.map((f, j) => (j === i ? { ...f, ...patch } : f)))
  const move = (i: number, d: -1 | 1): void => {
    const j = i + d
    if (j < 0 || j >= filters.length) return
    const next = [...filters]
    ;[next[i], next[j]] = [next[j], next[i]]
    setFilters(next)
  }
  const setControl = (i: number, control: FilterDef["control"]): void => {
    const f = filters[i]
    update(i, {
      control,
      defaultSelection:
        control === "single"
          ? f.defaultSelection.slice(0, 1)
          : f.defaultSelection,
    })
    // A single-choice filter applies one category, so trim the live choice.
    const current = store.getState().filterSelections[f.id] ?? []
    if (control === "single" && current.length > 1) {
      setFilterSelection(f.id, current.slice(0, 1))
    }
  }
  const setDefault = (i: number, defaultSelection: string[]): void => {
    update(i, { defaultSelection })
    // The author is choosing what viewers see first: show it now too.
    setFilterSelection(filters[i].id, defaultSelection)
  }
  const add = (variable: string): void => {
    if (!variable) return
    if (filters.some((f) => f.variable === variable)) {
      setNote(`${variable} is already a filter.`)
      return
    }
    if (
      codebook !== null &&
      getBoolChildLabels(codebook, variable).length === 0
    ) {
      setNote(`${variable} has no categories to filter by.`)
      return
    }
    setFilters([
      ...filters,
      {
        id: newId("filter"),
        label: lastSegment(variable),
        variable,
        control: "multi",
        defaultSelection: [],
      },
    ])
    setNewVariable(undefined)
    setNote(null)
  }
  const acceptsDrag = (e: React.DragEvent): boolean =>
    e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE)

  return (
    <ConfigProvider theme={WORKSPACE_THEME}>
      <Modal
        open={open}
        onCancel={onClose}
        onOk={onClose}
        title="Filters viewers can use"
        okText="Done"
        cancelButtonProps={{ style: { display: "none" } }}
        width={640}
        mask={false}
        rootClassName="dq-workspace dq-filterdef-modal"
        afterClose={() => {
          setNewVariable(undefined)
          setNote(null)
        }}
      >
        <div
          className="dq-filterdefs"
          data-drop={dropping || undefined}
          onDragOver={(e) => {
            if (!acceptsDrag(e)) return
            e.preventDefault()
            e.dataTransfer.dropEffect = "copy"
            if (!dropping) setDropping(true)
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setDropping(false)
            }
          }}
          onDrop={(e) => {
            setDropping(false)
            const v = e.dataTransfer.getData(QC_BUILDER_DRAG_TYPE)
            if (!v) return
            e.preventDefault()
            add(v)
          }}
        >
          {filters.length === 0 ? (
            <p className="dq-filterdefs__empty">
              No filters yet. Pick a variable below or drop one from the tree.
            </p>
          ) : (
            <ol className="dq-filterdefs__list" aria-label="Filters">
              {filters.map((f, i) => (
                <li key={f.id} className="dq-filterdef">
                  <Input
                    size="small"
                    className="dq-filterdef__label"
                    aria-label="Filter label"
                    value={f.label}
                    placeholder={lastSegment(f.variable)}
                    onChange={(e) => update(i, { label: e.target.value })}
                  />
                  <span className="dq-filterdef__var" title={f.variable}>
                    {f.variable}
                  </span>
                  <Segmented
                    size="small"
                    className="dq-filterdef__control"
                    value={f.control}
                    options={CONTROL_OPTIONS}
                    onChange={(v) => setControl(i, v)}
                  />
                  <Select
                    size="small"
                    className="dq-filterdef__default"
                    aria-label="Default"
                    mode={f.control === "multi" ? "multiple" : undefined}
                    allowClear
                    maxTagCount="responsive"
                    placeholder="Default: all"
                    options={categoriesById[f.id] ?? []}
                    value={
                      f.control === "multi"
                        ? f.defaultSelection
                        : f.defaultSelection[0]
                    }
                    onChange={(v: string | string[] | undefined | null) =>
                      setDefault(i, toValues(v))
                    }
                  />
                  <Button
                    size="small"
                    type="text"
                    className="dq-filterdef__up"
                    aria-label="Move up"
                    disabled={i === 0}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    onClick={() => move(i, -1)}
                  />
                  <Button
                    size="small"
                    type="text"
                    className="dq-filterdef__down"
                    aria-label="Move down"
                    disabled={i === filters.length - 1}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    onClick={() => move(i, 1)}
                  />
                  <Button
                    size="small"
                    type="text"
                    danger
                    className="dq-filterdef__remove"
                    aria-label="Remove filter"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() =>
                      setFilters(filters.filter((_, j) => j !== i))
                    }
                  />
                </li>
              ))}
            </ol>
          )}
          <div className="dq-filterdefs__add">
            <Select
              showSearch
              size="small"
              aria-label="Add filter variable"
              placeholder="Add a variable"
              options={variables}
              value={newVariable}
              onChange={(v: string) => {
                setNewVariable(v)
                setNote(null)
              }}
              optionFilterProp="label"
              notFoundContent={
                codebook === null
                  ? "Preparing the data"
                  : "No variable with categories matches"
              }
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button
              size="small"
              onClick={() => newVariable && add(newVariable)}
              disabled={!newVariable}
            >
              Add filter
            </Button>
          </div>
          {note && (
            <p className="dq-filterdefs__note" role="status">
              {note}
            </p>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  )
}

export default FilterDefinitionPopover
